import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { supabase } from "@/integrations/supabase/client";
import { resolveAvatarUrl } from "@/lib/avatar";
import smartChatLogo from "@/assets/smart-chat-logo.png.asset.json";
import { useUserRole } from "@/hooks/use-user-role";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Send, ImagePlus, X, MoreVertical, Trash2, Reply, ArrowLeft,
  Users as UsersIcon, Plus, Search, MessagesSquare, UserMinus, Loader2, Check, CheckCheck,
  Copy, Forward, Camera, Mic, Play, Pause, MapPin, Radio, Paperclip, Navigation,
  Smile, Phone, Video, PhoneOutgoing, PhoneIncoming,
} from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import { callBus } from "@/components/chat/call-bus";
import { useUnreadChats, markRoomRead } from "@/hooks/use-unread-chats";
import { useHistoryBackClose } from "@/hooks/use-history-back-close";

export const Route = createFileRoute("/_authenticated/chat")({
  validateSearch: (search: Record<string, unknown>) => ({
    room: typeof search.room === "string" ? search.room : undefined,
  }),
  component: ChatPage,
});

type Profile = { id: string; full_name: string | null; email: string | null; avatar_url: string | null; avatarSigned?: string | null; last_seen_at?: string | null };
type MemberProfile = Profile & { user_id?: string };
type Room = { id: string; name: string; is_global: boolean; is_dm: boolean; icon_url: string | null; created_at: string; updated_at: string; dmPartner?: Profile | null };

type Contact = { user_id: string; full_name: string | null; email: string | null; avatar_url: string | null; avatarSigned?: string | null; role: string | null };
type LastMessage = { content: string | null; image_url: string | null; audio_url: string | null; location_type: string | null; user_id: string; created_at: string; sender_name?: string | null };
type Message = {
  id: string;
  room_id: string;
  user_id: string;
  content: string | null;
  image_url: string | null;
  audio_url: string | null;
  location_lat: number | null;
  location_lng: number | null;
  location_type: string | null;
  location_expires_at: string | null;
  reply_to_id: string | null;
  deleted_at: string | null;
  created_at: string;
};
type TypingUser = { user_id: string; name: string; avatar: string | null; ts: number };

const ONLINE_LAST_SEEN_GRACE_MS = 45 * 1000;

function initialsOf(name: string | null | undefined) {
  if (!name) return "U";
  return name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function formatLastSeen(iso: string | null | undefined): string {
  if (!iso) return "Sedang tidak aktif";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Sedang tidak aktif";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Terakhir dilihat baru saja";
  if (mins < 60) return `Terakhir dilihat ${mins} menit lalu`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Terakhir dilihat ${hours} jam lalu`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Terakhir dilihat kemarin";
  if (days < 7) return `Terakhir dilihat ${days} hari lalu`;
  const d = new Date(iso);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const opts: Intl.DateTimeFormatOptions = sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" };
  return `Terakhir dilihat ${d.toLocaleDateString("id-ID", opts)}`;
}

function isRecentlyActive(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const then = new Date(iso).getTime();
  return !Number.isNaN(then) && Date.now() - then <= ONLINE_LAST_SEEN_GRACE_MS;
}


function profileDisplayName(profile: Profile | null | undefined) {
  return profile?.full_name || profile?.email || null;
}

function firstDisplayName(name: string | null | undefined) {
  return name?.trim().split(/\s+/)[0] || null;
}

function sameLastMessage(a: LastMessage | undefined, b: LastMessage) {
  return !!a
    && a.content === b.content
    && a.image_url === b.image_url
    && a.audio_url === b.audio_url
    && a.location_type === b.location_type
    && a.user_id === b.user_id
    && a.created_at === b.created_at
    && (a.sender_name ?? null) === (b.sender_name ?? null);
}

function normalizeMemberProfiles(data: unknown): Profile[] {
  if (!Array.isArray(data)) return [];
  const normalized = data
    .map((member): Profile | null => {
      const item = member as MemberProfile;
      const id = item.id ?? item.user_id;
      if (!id) return null;
      return {
        id,
        full_name: item.full_name ?? null,
        email: item.email ?? null,
        avatar_url: item.avatar_url ?? null,
        avatarSigned: item.avatarSigned ?? null,
      };
    });
  return normalized.filter((member): member is Profile => member !== null);
}

const roomIconUrlCache = new Map<string, string>();
async function resolveRoomIcon(value: string | null): Promise<string | null> {
  if (!value) return null;
  if (value.startsWith("http") || value.startsWith("/")) return value;
  const cached = roomIconUrlCache.get(value);
  if (cached) return cached;
  const { data } = await supabase.storage.from("chat-images").createSignedUrl(value, 60 * 60 * 24 * 7);
  if (!data?.signedUrl) return null;
  roomIconUrlCache.set(value, data.signedUrl);
  return data.signedUrl;
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Hari ini";
  if (d.toDateString() === yest.toDateString()) return "Kemarin";
  return d.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function parseCallActivity(content: string | null) {
  if (!content) return null;
  if (content.startsWith("📹 Panggilan video")) return { icon: "video" as const, text: content.replace(/^📹\s*/, "") };
  if (content.startsWith("📞 Panggilan suara")) return { icon: "audio" as const, text: content.replace(/^📞\s*/, "") };
  return null;
}

function ChatPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/chat" });
  const { role, isAdmin } = useUserRole();
  const [me, setMe] = useCachedState<Profile | null>("chat:me", null);
  const [rooms, setRooms] = useCachedState<Room[]>("chat:rooms", []);
  const [contacts, setContacts] = useCachedState<Contact[]>("chat:contacts", []);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [showRoomsMobile, setShowRoomsMobile] = useState(true);
  const [profilesCache, setProfilesCache] = useCachedState<Record<string, Profile>>("chat:profiles", {});
  const [openingDm, setOpeningDm] = useState<string | null>(null);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const [lastMessages, setLastMessages] = useCachedState<Record<string, LastMessage>>("chat:lastMessages", {});
  const profilesCacheRef = useRef<Record<string, Profile>>({});

  // Back-button / swipe-back on mobile should close the open room and return
  // to the room list, instead of leaving the chat page entirely.
  const isMobileRoomOpen =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches &&
    !!activeRoomId &&
    !showRoomsMobile;
  useHistoryBackClose(isMobileRoomOpen, () => {
    setActiveRoomId(null);
    setShowRoomsMobile(true);
  });
  const closeActiveRoom = useCallback(() => {
    setActiveRoomId(null);
    setShowRoomsMobile(true);
    navigate({ to: "/chat", search: {}, replace: true });
  }, [navigate]);

  useEffect(() => {
    profilesCacheRef.current = profilesCache;
  }, [profilesCache]);

  const cacheProfiles = useCallback(async (userIds: string[]) => {
    const missing = Array.from(new Set(userIds.filter((id) => id && !profilesCacheRef.current[id])));
    if (missing.length === 0) return profilesCacheRef.current;
    const { data } = await supabase.from("profiles").select("id, full_name, email, avatar_url").in("id", missing);
    if (!data || data.length === 0) return profilesCacheRef.current;
    const updates: Record<string, Profile> = {};
    for (const p of data) {
      const signed = await resolveAvatarUrl(p.avatar_url);
      updates[p.id] = { id: p.id, full_name: p.full_name, email: p.email, avatar_url: p.avatar_url, avatarSigned: signed };
    }
    if (Object.keys(updates).length > 0) {
      setProfilesCache((prev) => {
        const next = { ...prev, ...updates };
        profilesCacheRef.current = next;
        return next;
      });
      profilesCacheRef.current = { ...profilesCacheRef.current, ...updates };
    }
    return profilesCacheRef.current;
  }, [setProfilesCache]);

  // Load self
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: p } = await supabase.from("profiles").select("id, full_name, email, avatar_url").eq("id", user.id).maybeSingle();
      const signed = await resolveAvatarUrl(p?.avatar_url);
      setMe({ id: user.id, full_name: p?.full_name ?? null, email: p?.email ?? user.email ?? null, avatar_url: p?.avatar_url ?? null, avatarSigned: signed });
    })();
  }, []);

  // Load rooms (with DM partner resolution)
  const loadRooms = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data, error } = await supabase
      .from("chat_rooms")
      .select("id, name, is_global, is_dm, icon_url, created_at, updated_at")
      .order("is_dm", { ascending: true })
      .order("is_global", { ascending: false })
      .order("updated_at", { ascending: false });

    if (error) { toast.error("Gagal memuat ruang chat"); return; }
    const baseRooms = (data ?? []) as Room[];

    // Resolve DM partner info
    const dmIds = baseRooms.filter((r) => r.is_dm).map((r) => r.id);
    const dmPartners: Record<string, Profile | null> = {};
    if (dmIds.length > 0) {
      const { data: members } = await supabase
        .from("chat_room_members")
        .select("room_id, user_id")
        .in("room_id", dmIds);
      const otherIds = Array.from(new Set((members ?? []).filter((m) => m.user_id !== user.id).map((m) => m.user_id)));
      if (otherIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles").select("id, full_name, email, avatar_url, last_seen_at").in("id", otherIds);
        const profMap: Record<string, Profile> = {};
        for (const p of profs ?? []) {
          const signed = await resolveAvatarUrl(p.avatar_url);
          profMap[p.id] = { id: p.id, full_name: p.full_name, email: p.email, avatar_url: p.avatar_url, avatarSigned: signed, last_seen_at: p.last_seen_at ?? null };
        }

        for (const m of members ?? []) {
          if (m.user_id !== user.id && profMap[m.user_id]) dmPartners[m.room_id] = profMap[m.user_id];
        }
      }
    }
    const enriched = await Promise.all(baseRooms.map(async (r) => {
      const withDm = r.is_dm ? { ...r, dmPartner: dmPartners[r.id] ?? null } : r;
      const icon = await resolveRoomIcon(withDm.icon_url);
      return { ...withDm, icon_url: icon };
    }));
    setRooms((prev) => {
      const prevById = new Map(prev.map((r) => [r.id, r] as const));
      const merged = enriched.map((r) => {
        const p = prevById.get(r.id);
        if (!p) return r;
        // Preserve reference if visible fields are equal so React doesn't
        // remount rows on every message (updated_at changes on each insert).
        const sameShallow =
          p.name === r.name &&
          p.is_global === r.is_global &&
          p.is_dm === r.is_dm &&
          p.icon_url === r.icon_url &&
          (p.dmPartner?.id ?? null) === (r.dmPartner?.id ?? null) &&
          (p.dmPartner?.avatarSigned ?? null) === (r.dmPartner?.avatarSigned ?? null) &&
          (p.dmPartner?.full_name ?? null) === (r.dmPartner?.full_name ?? null) &&
          (p.dmPartner?.last_seen_at ?? null) === (r.dmPartner?.last_seen_at ?? null);

        return sameShallow ? p : r;
      });
      // If the set of room ids is unchanged and all refs preserved, keep prev
      // array reference — avoids downstream effects (last-messages fetch,
      // realtime re-subscribe) firing on cosmetic re-order alone.
      const prevIds = prev.map((r) => r.id).slice().sort().join(",");
      const nextIds = merged.map((r) => r.id).slice().sort().join(",");
      if (
        prevIds === nextIds &&
        merged.every((r) => prev.some((pr) => pr === r))
      ) {
        return prev;
      }
      return merged;
    });
  }, []);

  // Load contacts
  const loadContacts = useCallback(async () => {
    const { data, error } = await supabase.rpc("list_chat_contacts");
    if (error) return;
    const list = (data ?? []) as Contact[];
    const resolved = await Promise.all(list.map(async (c) => ({ ...c, avatarSigned: await resolveAvatarUrl(c.avatar_url) })));
    setContacts(resolved);
  }, []);

  useEffect(() => { loadRooms(); loadContacts(); }, [loadRooms, loadContacts]);

  useEffect(() => {
    if (!search.room || !rooms.some((room) => room.id === search.room)) return;
    setActiveRoomId(search.room);
    setShowRoomsMobile(false);
  }, [search.room, rooms]);

  const dmPartnerIdsKey = useMemo(
    () => rooms.map((room) => room.dmPartner?.id).filter((id): id is string => Boolean(id)).slice().sort().join(","),
    [rooms],
  );

  const refreshDmLastSeen = useCallback(async () => {
    const ids = dmPartnerIdsKey.split(",").filter(Boolean);
    if (ids.length === 0) return;
    const { data } = await supabase.from("profiles").select("id, last_seen_at").in("id", ids);
    if (!data || data.length === 0) return;
    const lastSeenById = new Map(data.map((profile) => [profile.id, profile.last_seen_at ?? null] as const));
    setRooms((prev) => {
      let changed = false;
      const next = prev.map((room) => {
        const partnerId = room.dmPartner?.id;
        if (!partnerId || !lastSeenById.has(partnerId)) return room;
        const nextLastSeen = lastSeenById.get(partnerId) ?? null;
        if ((room.dmPartner?.last_seen_at ?? null) === nextLastSeen) return room;
        changed = true;
        return { ...room, dmPartner: { ...room.dmPartner!, last_seen_at: nextLastSeen } };
      });
      return changed ? next : prev;
    });
  }, [dmPartnerIdsKey, setRooms]);

  useEffect(() => {
    if (!dmPartnerIdsKey) return;
    refreshDmLastSeen();
    const iv = window.setInterval(refreshDmLastSeen, 2000);
    return () => window.clearInterval(iv);
  }, [dmPartnerIdsKey, refreshDmLastSeen]);

  useEffect(() => {
    if (!dmPartnerIdsKey) return;
    const partnerIds = new Set(dmPartnerIdsKey.split(",").filter(Boolean));
    const ch = supabase
      .channel(`profiles-last-seen-${dmPartnerIdsKey}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "profiles" }, (payload) => {
        const profile = payload.new as { id?: string; last_seen_at?: string | null };
        if (!profile.id || !partnerIds.has(profile.id)) return;
        setRooms((prev) => {
          let changed = false;
          const next = prev.map((room) => {
            const partner = room.dmPartner;
            if (!partner || partner.id !== profile.id) return room;
            const nextLastSeen = profile.last_seen_at ?? null;
            if ((partner.last_seen_at ?? null) === nextLastSeen) return room;
            changed = true;
            return { ...room, dmPartner: { ...partner, last_seen_at: nextLastSeen } };
          });
          return changed ? next : prev;
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [dmPartnerIdsKey, setRooms]);

  // Open DM with a contact
  const openDm = useCallback(async (otherId: string) => {
    setOpeningDm(otherId);
    const { data, error } = await supabase.rpc("get_or_create_dm", { other_user_id: otherId });
    setOpeningDm(null);
    if (error || !data) { toast.error("Gagal membuka chat"); return; }
    await loadRooms();
    setActiveRoomId(data as string);
    setShowRoomsMobile(false);
  }, [loadRooms]);

  // Realtime rooms list
  useEffect(() => {
    const ch = supabase
      .channel("chat_rooms_list")
      .on("postgres_changes", { event: "*", schema: "public", table: "chat_rooms" }, () => loadRooms())
      .on("postgres_changes", { event: "*", schema: "public", table: "chat_room_members" }, () => loadRooms())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [loadRooms]);

  // Load last message per room (WhatsApp-style subtitle)
  const loadLastMessages = useCallback(async (roomIds: string[]) => {
    if (roomIds.length === 0) return;
    const { data } = await supabase
      .from("chat_messages")
      .select("room_id, content, image_url, audio_url, location_type, user_id, created_at")
      .in("room_id", roomIds)
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(500);
    if (!data) return;
    const map: Record<string, LastMessage> = {};
    for (const m of data) {
      if (!map[m.room_id]) map[m.room_id] = { content: m.content, image_url: m.image_url, audio_url: m.audio_url, location_type: m.location_type, user_id: m.user_id, created_at: m.created_at };
    }
    const profiles = await cacheProfiles(Object.values(map).map((m) => m.user_id));
    for (const key of Object.keys(map)) {
      map[key] = { ...map[key], sender_name: profileDisplayName(profiles[map[key].user_id]) };
    }
    setLastMessages((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of Object.keys(map)) {
        if (!sameLastMessage(prev[key], map[key])) {
          next[key] = map[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [cacheProfiles, setLastMessages]);

  // Stable key of room ids — decoupled from array-reference / order churn so
  // that group rooms (whose `updated_at` changes on every incoming message)
  // don't trigger re-fetches and realtime re-subscriptions that flash the UI.
  const roomIdsKey = useMemo(
    () => rooms.map((r) => r.id).slice().sort().join(","),
    [rooms],
  );
  const roomIdsRef = useRef<string[]>([]);
  useEffect(() => { roomIdsRef.current = rooms.map((r) => r.id); }, [rooms]);

  useEffect(() => {
    if (!roomIdsKey) return;
    loadLastMessages(roomIdsKey.split(","));
  }, [roomIdsKey, loadLastMessages]);

  useEffect(() => {
    const refreshChatData = () => {
      loadRooms();
      loadContacts();
      if (roomIdsRef.current.length > 0) loadLastMessages(roomIdsRef.current);
    };
    window.addEventListener("almunajah:data-refresh", refreshChatData);
    return () => window.removeEventListener("almunajah:data-refresh", refreshChatData);
  }, [loadRooms, loadContacts, loadLastMessages]);

  // Realtime updates for last messages
  useEffect(() => {
    const ch = supabase
      .channel("chat_last_messages")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages" }, (payload) => {
        const m = payload.new as LastMessage & { room_id: string };
        const cachedName = profileDisplayName(profilesCacheRef.current[m.user_id]);
        const nextMessage: LastMessage = { content: m.content, image_url: m.image_url, audio_url: m.audio_url, location_type: m.location_type, user_id: m.user_id, created_at: m.created_at, sender_name: cachedName };
        setLastMessages((prev) => sameLastMessage(prev[m.room_id], nextMessage) ? prev : { ...prev, [m.room_id]: nextMessage });
        if (!cachedName) {
          cacheProfiles([m.user_id]).then((profiles) => {
            const senderName = profileDisplayName(profiles[m.user_id]);
            if (!senderName) return;
            setLastMessages((prev) => {
              const current = prev[m.room_id];
              if (!current || current.created_at !== m.created_at || current.user_id !== m.user_id || current.sender_name === senderName) return prev;
              return { ...prev, [m.room_id]: { ...current, sender_name: senderName } };
            });
          });
        }
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "chat_messages" }, () => {
        // refresh full map on delete to keep accuracy
        if (roomIdsRef.current.length > 0) loadLastMessages(roomIdsRef.current);
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [cacheProfiles, loadLastMessages, setLastMessages]);


  // App-wide presence — observe users who currently have any Al-Munajah page open.
  useEffect(() => {
    if (!me?.id) return;
    const ch = supabase.channel("chat_presence", { config: { presence: { key: me.id } } });
    ch.on("presence", { event: "sync" }, () => {
      const state = ch.presenceState() as Record<string, Array<{ user_id: string }>>;
      const ids = new Set<string>();
      for (const k of Object.keys(state)) {
        for (const e of state[k]) if (e.user_id) ids.add(e.user_id);
      }
      setOnlineUserIds(ids);
    }).subscribe();
    // Heartbeat + online tracking now handled globally in AppShell so the
    // user counts as "online" while anywhere in the Al-Munajah app.
    return () => {
      supabase.removeChannel(ch);
    };

  }, [me?.id]);


  // Profile resolver
  const ensureProfiles = useCallback(async (userIds: string[]) => {
    await cacheProfiles(userIds);
  }, [cacheProfiles]);

  // Ensure profiles for last-message senders (for group-chat "Name: text" preview)
  useEffect(() => {
    const ids = Object.values(lastMessages).map((m) => m.user_id);
    if (ids.length > 0) ensureProfiles(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessages]);

  const activeRoom = rooms.find((r) => r.id === activeRoomId) ?? null;
  const { perRoom: unreadPerRoom } = useUnreadChats();

  // Mark active room as read whenever it changes or a new message arrives while open
  useEffect(() => {
    if (!activeRoomId) return;
    markRoomRead(activeRoomId);
  }, [activeRoomId, lastMessages]);

  const startCall = useCallback((peer: { id: string; name: string; avatar: string | null }, mode: "audio" | "video") => {
    callBus.start({ peerId: peer.id, peerName: peer.name, peerAvatar: peer.avatar, mode, roomId: activeRoomId ?? undefined });
  }, [activeRoomId]);

  return (
    <div className="flex h-full min-h-0 flex-col md:h-[calc(100dvh-3.5rem)]">
      <div className="flex flex-1 overflow-hidden md:rounded-2xl md:border bg-card md:shadow-[var(--shadow-elevation-2)]">
        {/* Rooms panel */}
        <aside
          className={cn(
            "flex flex-col w-full md:w-80 md:max-w-xs border-r bg-gradient-to-b from-primary/5 to-card",
            !showRoomsMobile && "hidden md:flex"
          )}
        >
          <div className="p-4 border-b">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <img
                  src={smartChatLogo.url}
                  alt="Smart Chat"
                  width={40}
                  height={40}
                  loading="lazy"
                  className="w-10 h-10 rounded-xl object-contain drop-shadow-sm"
                />
                <div>
                  <div className="font-bold text-sm">Smart Chat</div>
                  <div className="text-[11px] text-muted-foreground">Al-Munajah</div>
                </div>
              </div>
              {isAdmin && <CreateRoomDialog onCreated={loadRooms} />}
            </div>
          </div>
          <ScrollArea className="flex-1">
            <ul className="p-2 space-y-1">
              {rooms.map((r) => {
                const displayName = r.is_dm ? (r.dmPartner?.full_name || r.dmPartner?.email || "Pengguna") : r.name;
                const avatarSrc = r.is_dm ? r.dmPartner?.avatarSigned : r.icon_url;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveRoomId(r.id);
                        setShowRoomsMobile(false);
                        navigate({ to: "/chat", search: { room: r.id }, replace: true });
                      }}
                      className={cn(
                        "w-full text-left flex items-center gap-3 p-2.5 rounded-xl transition-colors",
                        activeRoomId === r.id ? "bg-muted" : "hover:bg-muted/60"
                      )}
                    >
                      <Avatar className="w-10 h-10 shrink-0">
                        {avatarSrc && <AvatarImage src={avatarSrc} />}
                        <AvatarFallback className="bg-primary/15 text-primary font-bold text-sm">
                          {r.is_global ? <UsersIcon className="w-5 h-5" /> : initialsOf(displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold truncate">{displayName}</div>
                          {lastMessages[r.id] && (
                            <div className="text-[10px] text-muted-foreground shrink-0">
                              {formatTime(lastMessages[r.id].created_at)}
                            </div>
                          )}
                        </div>
                        <div className="mt-0.5 text-[11px] truncate text-muted-foreground flex items-center justify-between gap-2">
                          <span className="truncate">
                          {(() => {
                            const lm = lastMessages[r.id];
                            if (!lm) return r.is_dm ? "Chat pribadi" : "Grup";
                            const isMine = me?.id === lm.user_id;
                            const senderName = isMine
                              ? "Anda"
                              : (!r.is_dm ? (firstDisplayName(lm.sender_name) ?? firstDisplayName(profileDisplayName(profilesCache[lm.user_id])) ?? "Pengguna") : "");
                            const prefix = senderName ? `${senderName}: ` : "";
                            if (lm.content && lm.content.trim().length > 0) {
                              const ca = parseCallActivity(lm.content);
                              if (ca) {
                                const iconCls = isMine ? "w-3.5 h-3.5 text-muted-foreground shrink-0" : "w-3.5 h-3.5 text-green-600 shrink-0";
                                const callLabel = ca.icon === "video" ? "Telepon video" : "Telepon suara";
                                const iconEl = ca.icon === "video" ? (
                                  <svg className={iconCls} viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <rect x="2" y="6" width="14" height="12" rx="2" ry="2" />
                                    <path d="M22 8l-6 4 6 4V8z" />
                                    {isMine
                                      ? <path d="M6 14l6-6M7 8h5v5" />
                                      : <path d="M12 8l-6 6M11 14H6V9" />}
                                  </svg>
                                ) : (isMine ? <PhoneOutgoing className={iconCls} /> : <PhoneIncoming className={iconCls} />);
                                return (
                                  <span className="inline-flex items-center gap-1 align-middle">
                                    {iconEl}
                                    <span>{callLabel}</span>
                                  </span>
                                );
                              }
                              return `${prefix}${lm.content}`;
                            }
                            if (lm.audio_url) {
                              return (
                                <span className="inline-flex items-center gap-1 align-middle">
                                  {prefix}
                                  <Mic className="w-3.5 h-3.5 text-primary shrink-0" />
                                  <span>Pesan suara</span>
                                </span>
                              );
                            }
                            const fallback = lm.location_type === "live"
                              ? "🟢 Live location"
                              : lm.location_type === "point"
                                ? "📍 Lokasi"
                                : lm.image_url ? "📷 Foto" : "";
                            return `${prefix}${fallback}`;
                          })()}
                          </span>
                          {unreadPerRoom[r.id] > 0 && activeRoomId !== r.id && (
                            <span className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                              {unreadPerRoom[r.id] > 99 ? "99+" : unreadPerRoom[r.id]}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
              {rooms.length === 0 && (
                <li className="text-center text-sm text-muted-foreground py-8">Belum ada ruang chat</li>
              )}

              {contacts.map((c) => {
                const hasDm = rooms.some((r) => r.is_dm && r.dmPartner?.id === c.user_id);
                if (hasDm) return null;
                const name = c.full_name || c.email || "Pengguna";
                return (
                  <li key={c.user_id}>
                    <button
                      type="button"
                      disabled={openingDm === c.user_id}
                      onClick={() => openDm(c.user_id)}
                      className="w-full text-left flex items-center gap-3 p-2.5 rounded-xl hover:bg-primary/10 transition-all disabled:opacity-60"
                    >
                      <Avatar className="w-10 h-10 shrink-0">
                        {c.avatarSigned && <AvatarImage src={c.avatarSigned} />}
                        <AvatarFallback className="bg-primary/15 text-primary font-bold text-sm">
                          {initialsOf(name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{name}</div>
                        <div className="text-[11px] truncate text-muted-foreground capitalize">{c.role ?? "staff"}</div>
                      </div>
                      {openingDm === c.user_id && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        </aside>

        {/* Messages panel */}
        <section className={cn("flex-1 min-h-0 flex flex-col bg-[radial-gradient(circle_at_top,_theme(colors.primary/8%),_transparent_50%)]", showRoomsMobile && "hidden md:flex")}>
          {activeRoom && me ? (
            <RoomView
              room={activeRoom}
              rooms={rooms}
              onlineUserIds={onlineUserIds}
              me={me}
              isAdmin={isAdmin}
              role={role}
              profilesCache={profilesCache}
              ensureProfiles={ensureProfiles}
              onBack={closeActiveRoom}
              onStartCall={startCall}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Pilih ruang untuk memulai
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ============================ ROOM VIEW ============================ */

function RoomView({
  room, rooms, onlineUserIds, me, isAdmin, role, profilesCache, ensureProfiles, onBack, onStartCall,
}: {
  room: Room;
  rooms: Room[];
  onlineUserIds: Set<string>;
  me: Profile;
  isAdmin: boolean;
  role: string | null;
  profilesCache: Record<string, Profile>;
  ensureProfiles: (ids: string[]) => Promise<void>;
  onBack: () => void;
  onStartCall: (peer: { id: string; name: string; avatar: string | null }, mode: "audio" | "video") => void;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [attaching, setAttaching] = useState<File | null>(null);
  const [actionMsg, setActionMsg] = useState<Message | null>(null);
  const [forwardMsg, setForwardMsg] = useState<Message | null>(null);
  const [forwarding, setForwarding] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const lastTypedAtRef = useRef<number>(0);
  const liveShareRef = useRef<{ messageId: string; watchId: number; expiresAt: number } | null>(null);
  const [liveShare, setLiveShare] = useState<{ messageId: string; expiresAt: number } | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<Message | null>(null);

  // Load messages
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("id, room_id, user_id, content, image_url, audio_url, location_lat, location_lng, location_type, location_expires_at, reply_to_id, deleted_at, created_at")
        .eq("room_id", room.id)
        .order("created_at", { ascending: true })
        .limit(200);
      if (cancelled) return;
      if (error) { toast.error("Gagal memuat pesan"); setLoading(false); return; }
      const msgs = (data ?? []) as Message[];
      setMessages(msgs);
      await ensureProfiles(Array.from(new Set(msgs.map((m) => m.user_id))));
      setLoading(false);
      setTimeout(() => scrollToBottom(true), 50);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.id]);

  useEffect(() => {
    let cancelled = false;
    const refreshMessages = async () => {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("id, room_id, user_id, content, image_url, audio_url, location_lat, location_lng, location_type, location_expires_at, reply_to_id, deleted_at, created_at")
        .eq("room_id", room.id)
        .order("created_at", { ascending: true })
        .limit(200);
      if (cancelled || error) return;
      const msgs = (data ?? []) as Message[];
      setMessages(msgs);
      await ensureProfiles(Array.from(new Set(msgs.map((m) => m.user_id))));
      setTimeout(() => scrollToBottom(true), 50);
    };
    window.addEventListener("almunajah:data-refresh", refreshMessages);
    return () => {
      cancelled = true;
      window.removeEventListener("almunajah:data-refresh", refreshMessages);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.id]);

  const scrollToBottom = (instant = false) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: instant ? "auto" : "smooth" });
  };

  // Realtime: messages + presence (typing)
  useEffect(() => {
    const channel = supabase.channel(`room:${room.id}`, {
      config: { presence: { key: me.id } },
    });
    channelRef.current = channel;

    channel
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages", filter: `room_id=eq.${room.id}` },
        async (payload) => {
          const m = payload.new as Message;
          setMessages((prev) => {
            if (prev.some((x) => x.id === m.id)) return prev;
            // Reconcile optimistic temp from self: same author + same content/image/audio
            const filtered = prev.filter((x) => {
              if (!x.id.startsWith("temp-")) return true;
              if (x.user_id !== m.user_id) return true;
              const sameContent = (x.content ?? null) === (m.content ?? null)
                && (x.image_url ?? null) === (m.image_url ?? null)
                && (x.audio_url ?? null) === (m.audio_url ?? null);
              return !sameContent;
            });
            return [...filtered, m];
          });
          await ensureProfiles([m.user_id]);
          setTimeout(() => scrollToBottom(), 50);
        }
      )
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "chat_messages", filter: `room_id=eq.${room.id}` },
        (payload) => {
          const m = payload.new as Message;
          setMessages((prev) => prev.map((x) => x.id === m.id ? m : x));
        }
      )
      .on("postgres_changes",
        { event: "DELETE", schema: "public", table: "chat_messages", filter: `room_id=eq.${room.id}` },
        (payload) => {
          const old = payload.old as { id: string };
          setMessages((prev) => prev.filter((x) => x.id !== old.id));
        }
      )
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState() as Record<string, Array<{ user_id: string; name: string; avatar: string | null; typing?: boolean; ts?: number }>>;
        const arr: TypingUser[] = [];
        const now = Date.now();
        for (const key of Object.keys(state)) {
          for (const entry of state[key]) {
            if (entry.typing && entry.user_id !== me.id && (now - (entry.ts ?? 0) < 6000)) {
              arr.push({ user_id: entry.user_id, name: entry.name, avatar: entry.avatar, ts: entry.ts ?? now });
            }
          }
        }
        setTypingUsers(arr);
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({ user_id: me.id, name: me.full_name ?? me.email ?? "Pengguna", avatar: me.avatarSigned ?? null, typing: false, ts: Date.now() });
        }
      });

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.id, me.id]);

  // Periodic typing-expiry refresh
  useEffect(() => {
    const t = setInterval(() => {
      setTypingUsers((arr) => arr.filter((u) => Date.now() - u.ts < 6000));
    }, 2000);
    return () => clearInterval(t);
  }, []);

  const broadcastTyping = useCallback((typing: boolean) => {
    const ch = channelRef.current;
    if (!ch) return;
    ch.track({ user_id: me.id, name: me.full_name ?? me.email ?? "Pengguna", avatar: me.avatarSigned ?? null, typing, ts: Date.now() });
  }, [me]);

  const onDraftChange = (val: string) => {
    setDraft(val);
  };

  const onTypingFocus = useCallback(() => {
    broadcastTyping(true);
    if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
  }, [broadcastTyping]);

  const onTypingBlur = useCallback(() => {
    broadcastTyping(false);
    if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
    lastTypedAtRef.current = 0;
  }, [broadcastTyping]);

  const uploadImage = async (file: File): Promise<string | null> => {
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
    const path = `${me.id}/${Date.now()}.${ext || "jpg"}`;
    const { error } = await supabase.storage.from("chat-images").upload(path, file, { contentType: file.type || "image/jpeg" });
    if (error) { toast.error("Upload gagal: " + error.message); return null; }
    return path;
  };

  const uploadAudio = async (blob: Blob, ext: string): Promise<string | null> => {
    const path = `${me.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("chat-audio").upload(path, blob, { contentType: blob.type || "audio/webm" });
    if (error) { toast.error("Upload suara gagal: " + error.message); return null; }
    return path;
  };

  const send = async () => {
    const content = draft.trim();
    if (!content && !attaching) return;
    setSending(true);
    let image_url: string | null = null;
    if (attaching) {
      image_url = await uploadImage(attaching);
      if (!image_url) { setSending(false); return; }
    }
    const currentReply = replyTo;
    // Optimistic message — clears input & appears instantly
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      id: tempId,
      room_id: room.id,
      user_id: me.id,
      content: content || null,
      image_url,
      audio_url: null,
      location_lat: null,
      location_lng: null,
      location_live_until: null,
      location_type: null,
      location_expires_at: null,
      deleted_at: null,
      reply_to_id: currentReply?.id ?? null,
      created_at: new Date().toISOString(),
    } as unknown as Message;
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    setAttaching(null);
    setReplyTo(null);
    broadcastTyping(false);
    if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
    setTimeout(() => scrollToBottom(), 30);

    const { data, error } = await supabase.from("chat_messages").insert({
      room_id: room.id,
      user_id: me.id,
      content: content || null,
      image_url,
      reply_to_id: currentReply?.id ?? null,
    }).select().single();
    if (error) {
      toast.error("Gagal mengirim: " + error.message);
      setMessages((prev) => prev.filter((x) => x.id !== tempId));
      setSending(false);
      return;
    }
    if (data) {
      const real = data as Message;
      setMessages((prev) => {
        if (prev.some((x) => x.id === real.id)) return prev.filter((x) => x.id !== tempId);
        return prev.map((x) => (x.id === tempId ? real : x));
      });
    }
    setSending(false);
  };

  const sendVoice = async (blob: Blob, ext: string) => {
    setSending(true);
    const audio_url = await uploadAudio(blob, ext);
    if (!audio_url) { setSending(false); return; }
    const { error } = await supabase.from("chat_messages").insert({
      room_id: room.id,
      user_id: me.id,
      content: null,
      image_url: null,
      audio_url,
      reply_to_id: replyTo?.id ?? null,
    });
    if (error) { toast.error("Gagal mengirim suara: " + error.message); setSending(false); return; }
    setReplyTo(null);
    setSending(false);
  };

  const describeGeoError = (err: unknown): string => {
    // GeolocationPositionError isn't an Error instance — String(err) yields
    // "[object GeolocationPositionError]". Map its code to a friendly ID msg.
    const e = err as { code?: number; message?: string } | null;
    if (e && typeof e.code === "number") {
      switch (e.code) {
        case 1: return "Izin lokasi ditolak. Aktifkan izin lokasi untuk aplikasi ini di pengaturan HP Anda.";
        case 2: return "Lokasi tidak tersedia. Pastikan GPS/Lokasi HP menyala dan sinyal cukup baik.";
        case 3: return "Waktu tunggu lokasi habis. Coba lagi di area dengan sinyal GPS lebih baik.";
        default: return e.message || "Terjadi kesalahan saat mengambil lokasi.";
      }
    }
    if (err instanceof Error) return err.message;
    return "Terjadi kesalahan saat mengambil lokasi.";
  };

  const getCurrentPosition = (opts?: PositionOptions): Promise<GeolocationPosition> =>
    new Promise((resolve, reject) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        reject(new Error("Perangkat tidak mendukung lokasi"));
        return;
      }
      // Try high-accuracy first, then fall back to network-based lookup if the
      // GPS fix fails or times out (common on Android indoors).
      navigator.geolocation.getCurrentPosition(
        resolve,
        (err) => {
          if (opts?.enableHighAccuracy && (err.code === 2 || err.code === 3)) {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              ...opts, enableHighAccuracy: false, timeout: 20000,
            });
          } else {
            reject(err);
          }
        },
        opts,
      );
    });

  const sendLocation = async () => {
    setSending(true);
    try {
      const pos = await getCurrentPosition({ enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 });
      const { error } = await supabase.from("chat_messages").insert({
        room_id: room.id,
        user_id: me.id,
        content: null,
        image_url: null,
        location_lat: pos.coords.latitude,
        location_lng: pos.coords.longitude,
        location_type: "point",
        reply_to_id: replyTo?.id ?? null,
      });
      if (error) throw error;
      setReplyTo(null);
    } catch (err) {
      toast.error("Gagal mengirim lokasi: " + describeGeoError(err));
    } finally {
      setSending(false);
    }
  };

  const stopLiveShare = useCallback(async (reason?: "expired" | "manual") => {
    const cur = liveShareRef.current;
    if (!cur) return;
    try { if (typeof navigator !== "undefined" && navigator.geolocation) navigator.geolocation.clearWatch(cur.watchId); } catch { /* noop */ }
    liveShareRef.current = null;
    setLiveShare(null);
    await supabase.from("chat_messages")
      .update({ location_expires_at: new Date().toISOString() })
      .eq("id", cur.messageId);
    if (reason === "manual") toast.success("Berbagi lokasi dihentikan");
  }, []);

  const sendLiveLocation = async (durationMinutes: number) => {
    if (liveShareRef.current) { toast.error("Sudah membagikan lokasi"); return; }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      toast.error("Perangkat tidak mendukung lokasi"); return;
    }
    setSending(true);
    try {
      const pos = await getCurrentPosition({ enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 });
      const expiresAt = new Date(Date.now() + durationMinutes * 60_000);
      const { data, error } = await supabase.from("chat_messages").insert({
        room_id: room.id,
        user_id: me.id,
        content: null,
        image_url: null,
        location_lat: pos.coords.latitude,
        location_lng: pos.coords.longitude,
        location_type: "live",
        location_expires_at: expiresAt.toISOString(),
        reply_to_id: replyTo?.id ?? null,
      }).select("id").maybeSingle();
      if (error || !data) throw (error ?? new Error("Insert gagal"));

      const watchId = navigator.geolocation.watchPosition(
        (p) => {
          const cur = liveShareRef.current;
          if (!cur || cur.messageId !== data.id) return;
          if (Date.now() >= cur.expiresAt) { stopLiveShare("expired"); return; }
          supabase.from("chat_messages")
            .update({ location_lat: p.coords.latitude, location_lng: p.coords.longitude })
            .eq("id", data.id)
            .then(() => { /* noop */ });
        },
        (err) => { toast.error("Live lokasi terhenti: " + describeGeoError(err)); stopLiveShare("manual"); },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
      );
      liveShareRef.current = { messageId: data.id, watchId, expiresAt: expiresAt.getTime() };
      setLiveShare({ messageId: data.id, expiresAt: expiresAt.getTime() });
      setReplyTo(null);
      toast.success(`Membagikan lokasi live selama ${durationMinutes} menit`);
    } catch (err) {
      toast.error("Gagal memulai live lokasi: " + describeGeoError(err));
    } finally {
      setSending(false);
    }
  };

  // Auto-stop when expired
  useEffect(() => {
    if (!liveShare) return;
    const t = window.setInterval(() => {
      if (liveShareRef.current && Date.now() >= liveShareRef.current.expiresAt) stopLiveShare("expired");
    }, 5000);
    return () => window.clearInterval(t);
  }, [liveShare, stopLiveShare]);

  // Stop on unmount / room change
  useEffect(() => () => { if (liveShareRef.current) stopLiveShare("manual"); }, [stopLiveShare]);

  // Load hidden ("delete for me") message IDs for this room
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const msgIds = messages.map((m) => m.id);
      if (msgIds.length === 0) { if (!cancelled) setHiddenIds(new Set()); return; }
      const { data } = await supabase
        .from("chat_message_hidden")
        .select("message_id")
        .eq("user_id", me.id)
        .in("message_id", msgIds);
      if (cancelled) return;
      setHiddenIds(new Set((data ?? []).map((r) => r.message_id as string)));
    })();
    return () => { cancelled = true; };
  }, [messages, me.id]);

  const deleteForMe = async (id: string) => {
    setHiddenIds((prev) => { const n = new Set(prev); n.add(id); return n; });
    const { error } = await supabase.from("chat_message_hidden").insert({ user_id: me.id, message_id: id });
    if (error) {
      setHiddenIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast.error("Gagal menghapus: " + error.message);
    } else {
      toast.success("Pesan dihapus untuk saya");
    }
  };

  const deleteForEveryone = async (id: string) => {
    const { error } = await supabase
      .from("chat_messages")
      .update({ deleted_at: new Date().toISOString(), content: null, image_url: null, audio_url: null, location_lat: null, location_lng: null, location_type: null, location_expires_at: null })
      .eq("id", id);
    if (error) toast.error("Gagal menghapus: " + error.message);
    else toast.success("Pesan dihapus untuk semua");
  };

  const copyMessage = async (m: Message) => {
    const text = m.content ?? "";
    if (!text) { toast.error("Tidak ada teks untuk disalin"); return; }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Pesan disalin");
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const forwardToRoom = async (targetRoomId: string) => {
    if (!forwardMsg) return;
    setForwarding(true);
    const { error } = await supabase.from("chat_messages").insert({
      room_id: targetRoomId,
      user_id: me.id,
      content: forwardMsg.content,
      image_url: forwardMsg.image_url,
      audio_url: forwardMsg.audio_url,
      location_lat: forwardMsg.location_lat,
      location_lng: forwardMsg.location_lng,
      location_type: forwardMsg.location_type === "live" ? "point" : forwardMsg.location_type,
      reply_to_id: null,
    });
    setForwarding(false);
    if (error) { toast.error("Gagal meneruskan: " + error.message); return; }
    toast.success("Pesan diteruskan");
    setForwardMsg(null);
  };

  // Group by day (skip messages hidden for me)
  const grouped = useMemo(() => {
    const groups: { dateKey: string; label: string; items: Message[] }[] = [];
    for (const m of messages) {
      if (hiddenIds.has(m.id)) continue;
      const dk = new Date(m.created_at).toDateString();
      let g = groups[groups.length - 1];
      if (!g || g.dateKey !== dk) {
        g = { dateKey: dk, label: formatDateLabel(m.created_at), items: [] };
        groups.push(g);
      }
      g.items.push(m);
    }
    return groups;
  }, [messages, hiddenIds]);

  return (
    <>
      {/* Header */}
      <header className="shrink-0 px-3 md:px-5 py-3 border-b bg-card flex items-center gap-2.5">
        <Button variant="ghost" size="icon" className="md:hidden h-9 w-9" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <Avatar className="w-10 h-10 shrink-0">
          {room.is_dm
            ? room.dmPartner?.avatarSigned && <AvatarImage src={room.dmPartner.avatarSigned} />
            : room.icon_url && <AvatarImage src={room.icon_url} />}
          <AvatarFallback className="bg-primary/15 text-primary font-bold">
            {room.is_global ? <UsersIcon className="w-5 h-5" /> : initialsOf(room.is_dm ? (room.dmPartner?.full_name || room.dmPartner?.email || "U") : room.name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm truncate">
            {room.is_dm ? (room.dmPartner?.full_name || room.dmPartner?.email || "Pengguna") : room.name}
          </div>
          {(() => {
            const partnerOnline = room.is_dm && room.dmPartner?.id
              ? isRecentlyActive(room.dmPartner.last_seen_at)
              : true;
            const statusText = typingUsers.length > 0
              ? `${typingUsers[0].name}${typingUsers.length > 1 ? ` +${typingUsers.length - 1}` : ""} sedang mengetik...`
              : room.is_dm
                ? (partnerOnline ? "Online" : formatLastSeen(room.dmPartner?.last_seen_at))
                : "Grup";
            const dotClass = typingUsers.length > 0
              ? "bg-primary"
              : room.is_dm
                ? (partnerOnline ? "bg-emerald-500" : "bg-muted-foreground/50")
                : "bg-emerald-500";
            return (
              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <span className={cn("w-1.5 h-1.5 rounded-full", dotClass)} />
                {statusText}
              </div>
            );
          })()}
        </div>
        {room.is_dm && room.dmPartner && (
          <>
            <Button
              variant="ghost" size="icon" className="h-9 w-9 text-primary"
              aria-label="Panggilan suara"
              onClick={() => onStartCall({
                id: room.dmPartner!.id,
                name: room.dmPartner!.full_name || room.dmPartner!.email || "Pengguna",
                avatar: room.dmPartner!.avatarSigned ?? null,
              }, "audio")}
            >
              <Phone className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost" size="icon" className="h-9 w-9 text-primary"
              aria-label="Panggilan video"
              onClick={() => onStartCall({
                id: room.dmPartner!.id,
                name: room.dmPartner!.full_name || room.dmPartner!.email || "Pengguna",
                avatar: room.dmPartner!.avatarSigned ?? null,
              }, "video")}
            >
              <Video className="w-5 h-5" />
            </Button>
          </>
        )}
        {!room.is_dm && <RoomMembersSheet room={room} isAdmin={isAdmin} me={me} />}
        {!room.is_dm && !room.is_global && isAdmin && (
          <RoomActionsMenu room={room} onDeleted={onBack} />
        )}
      </header>

      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto px-3 md:px-5 py-4 space-y-1">
        {loading && (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Memuat pesan...
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-3">
              <MessagesSquare className="w-8 h-8" />
            </div>
            <div className="font-semibold text-sm">Belum ada pesan</div>
            <div className="text-xs text-muted-foreground mt-1">Sapa anggota lain dengan pesan pertama 👋</div>
          </div>
        )}
        {grouped.map((g) => (
          <div key={g.dateKey} className="space-y-1">
            <div className="flex justify-center my-3">
              <span className="text-[10px] font-semibold uppercase tracking-wider bg-card border px-2.5 py-1 rounded-full text-muted-foreground shadow-sm">
                {g.label}
              </span>
            </div>
            {g.items.map((m, idx) => {
              const prev = idx > 0 ? g.items[idx - 1] : null;
              const sameAuthor = prev && prev.user_id === m.user_id && (new Date(m.created_at).getTime() - new Date(prev.created_at).getTime() < 5 * 60 * 1000);
              return (
                <MessageBubble
                  key={m.id}
                  message={m}
                  isMine={m.user_id === me.id}
                  author={profilesCache[m.user_id]}
                  groupedWithPrev={!!sameAuthor}
                  replyTarget={m.reply_to_id ? messages.find((x) => x.id === m.reply_to_id) : null}
                  replyAuthor={m.reply_to_id ? profilesCache[messages.find((x) => x.id === m.reply_to_id)?.user_id ?? ""] : undefined}
                  onLongPress={() => setActionMsg(m)}
                />

              );
            })}
          </div>
        ))}

        {/* Typing bar */}
        {typingUsers.length > 0 && (
          <div className="flex items-end gap-2 pt-1 pl-1">
            <div className="flex -space-x-2">
              {typingUsers.slice(0, 3).map((u) => (
                <Avatar key={u.user_id} className="w-6 h-6 ring-2 ring-card">
                  {u.avatar && <AvatarImage src={u.avatar} />}
                  <AvatarFallback className="bg-primary/15 text-primary text-[10px] font-bold">{initialsOf(u.name)}</AvatarFallback>
                </Avatar>
              ))}
            </div>
            <div className="bg-card border rounded-2xl rounded-bl-sm px-3 py-2 shadow-sm flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "240ms" }} />
            </div>
          </div>
        )}
      </div>

      {/* Reply preview */}
      {replyTo && (
        <div className="shrink-0 px-3 md:px-5 pt-2 pb-1 border-t bg-card">
          <div className="flex items-start gap-2 bg-primary/8 border-l-4 border-primary rounded-r-lg p-2">
            <Reply className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold text-primary">
                Membalas {profilesCache[replyTo.user_id]?.full_name ?? "Pengguna"}
              </div>
              <div className="text-xs text-muted-foreground truncate">{replyTo.content ?? "Gambar"}</div>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => setReplyTo(null)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Attach preview */}
      {attaching && (
        <div className="shrink-0 px-3 md:px-5 pt-2 pb-1 border-t bg-card">
          <div className="flex items-center gap-2 bg-muted rounded-lg p-2">
            <img src={URL.createObjectURL(attaching)} alt="lampiran" className="w-12 h-12 rounded-md object-cover" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium truncate">{attaching.name}</div>
              <div className="text-[11px] text-muted-foreground">{(attaching.size / 1024).toFixed(0)} KB</div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAttaching(null)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Live share banner */}
      {liveShare && (
        <div className="shrink-0 px-3 md:px-5 py-2 border-t bg-primary/10 flex items-center gap-2 text-primary text-xs">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
          </span>
          <span className="flex-1 truncate font-medium">
            Membagikan lokasi live · berakhir {new Date(liveShare.expiresAt).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
          </span>
          <Button variant="ghost" size="sm" className="h-7 text-primary hover:text-primary" onClick={() => stopLiveShare("manual")}>
            Hentikan
          </Button>
        </div>
      )}

      {/* Composer */}
      <div className="shrink-0 border-t bg-card p-2 md:p-3">
        <VoiceComposer
          draft={draft}
          onDraftChange={onDraftChange}
          onSend={send}
          onSendVoice={sendVoice}
          onSendLocation={sendLocation}
          onSendLiveLocation={sendLiveLocation}
          liveActive={!!liveShare}
          sending={sending}
          hasAttachment={!!attaching}
          onPickImage={(f) => {
            if (f.size > 5 * 1024 * 1024) { toast.error("Maksimal 5MB"); return; }
            setAttaching(f);
          }}
          onTypingFocus={onTypingFocus}
          onTypingBlur={onTypingBlur}
        />
      </div>

      {/* Action sheet (long-press) */}
      <Sheet open={!!actionMsg} onOpenChange={(o) => !o && setActionMsg(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl p-0 max-h-[60vh]">
          <SheetHeader className="px-5 pt-5 pb-2 text-left">
            <SheetTitle className="text-base">Aksi Pesan</SheetTitle>
            <SheetDescription className="line-clamp-2 text-xs">
              {actionMsg?.content ?? (actionMsg?.location_type === "live" ? "Live location" : (actionMsg?.location_lat != null ? "Lokasi" : (actionMsg?.audio_url ? "Pesan suara" : (actionMsg?.image_url ? "Gambar" : ""))))}
            </SheetDescription>
          </SheetHeader>
          <div className="p-2">
            {!actionMsg?.deleted_at && (
              <>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted text-left"
                  onClick={() => { if (actionMsg) { setReplyTo(actionMsg); setActionMsg(null); } }}
                >
                  <Reply className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium">Balas</span>
                </button>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted text-left"
                  onClick={() => { if (actionMsg) { setForwardMsg(actionMsg); setActionMsg(null); } }}
                >
                  <Forward className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium">Teruskan</span>
                </button>
                <button
                  type="button"
                  disabled={!actionMsg?.content}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted text-left disabled:opacity-50"
                  onClick={() => { if (actionMsg) { copyMessage(actionMsg); setActionMsg(null); } }}
                >
                  <Copy className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium">Salin</span>
                </button>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-destructive/10 text-left text-destructive"
                  onClick={() => { if (actionMsg) { setConfirmDelete(actionMsg); setActionMsg(null); } }}
                >
                  <Trash2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Hapus</span>
                </button>
              </>
            )}
            {actionMsg?.deleted_at && (
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-destructive/10 text-left text-destructive"
                onClick={() => { if (actionMsg) { deleteForMe(actionMsg.id); setActionMsg(null); } }}
              >
                <Trash2 className="w-5 h-5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Hapus untuk saya</div>
                  <div className="text-[11px] text-muted-foreground">Pesan hanya hilang di perangkat kamu.</div>
                </div>
              </button>
            )}
          </div>

        </SheetContent>
      </Sheet>

      {/* Delete choice sheet */}
      <Sheet open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="text-left">
            <SheetTitle className="text-base">Hapus pesan?</SheetTitle>
            <SheetDescription className="text-xs">
              Pilih cara menghapus pesan ini.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-3 space-y-2">
            <button
              type="button"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border hover:bg-muted text-left"
              onClick={() => { if (confirmDelete) { deleteForMe(confirmDelete.id); setConfirmDelete(null); } }}
            >
              <Trash2 className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <div className="text-sm font-medium">Hapus untuk saya</div>
                <div className="text-[11px] text-muted-foreground">Pesan hanya hilang di perangkat kamu.</div>
              </div>
            </button>
            {confirmDelete?.user_id === me.id && !confirmDelete?.deleted_at && (
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-destructive/30 hover:bg-destructive/10 text-left text-destructive"
                onClick={() => { if (confirmDelete) { deleteForEveryone(confirmDelete.id); setConfirmDelete(null); } }}
              >
                <Trash2 className="w-5 h-5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Hapus untuk semua</div>
                  <div className="text-[11px] opacity-80">Semua orang di ruang ini tidak akan melihat pesan ini lagi.</div>
                </div>
              </button>
            )}
            <button
              type="button"
              className="w-full px-4 py-3 rounded-xl hover:bg-muted text-sm font-medium"
              onClick={() => setConfirmDelete(null)}
            >
              Batal
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Forward sheet */}

      <Sheet open={!!forwardMsg} onOpenChange={(o) => !o && setForwardMsg(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl p-0 max-h-[80vh] flex flex-col">
          <SheetHeader className="px-5 pt-5 pb-3 text-left border-b">
            <SheetTitle className="text-base">Teruskan ke...</SheetTitle>
            <SheetDescription className="text-xs">Pilih ruang tujuan</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-2">
            {rooms.filter((r) => r.id !== room.id).map((r) => {
              const displayName = r.is_dm ? (r.dmPartner?.full_name || r.dmPartner?.email || "Pengguna") : r.name;
              return (
                <button
                  key={r.id}
                  type="button"
                  disabled={forwarding}
                  onClick={() => forwardToRoom(r.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted text-left disabled:opacity-60"
                >
                  <Avatar className="w-10 h-10">
                    {r.is_dm
                      ? r.dmPartner?.avatarSigned && <AvatarImage src={r.dmPartner.avatarSigned} />
                      : r.icon_url && <AvatarImage src={r.icon_url} />}
                    <AvatarFallback className="bg-primary/15 text-primary font-bold text-sm">
                      {r.is_global ? <UsersIcon className="w-5 h-5" /> : initialsOf(displayName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{displayName}</div>
                    <div className="text-[11px] text-muted-foreground">{r.is_dm ? "Chat pribadi" : "Grup"}</div>
                  </div>
                  {forwarding && <Loader2 className="w-4 h-4 animate-spin" />}
                </button>
              );
            })}
            {rooms.filter((r) => r.id !== room.id).length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-8">Tidak ada ruang tujuan</div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}


/* ============================ MESSAGE BUBBLE ============================ */

function MessageBubble({
  message, isMine, author, groupedWithPrev, replyTarget, replyAuthor, onLongPress,
}: {
  message: Message;
  isMine: boolean;
  author?: Profile;
  groupedWithPrev: boolean;
  replyTarget?: Message | null;
  replyAuthor?: Profile;
  onLongPress: () => void;
}) {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const pressTimerRef = useRef<number | null>(null);
  const triggeredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!message.image_url) return;
    (async () => {
      const { data } = await supabase.storage.from("chat-images").createSignedUrl(message.image_url!, 60 * 60 * 24);
      if (!cancelled) setImgUrl(data?.signedUrl ?? null);
    })();
    return () => { cancelled = true; };
  }, [message.image_url]);

  const startPress = () => {
    triggeredRef.current = false;
    if (pressTimerRef.current) window.clearTimeout(pressTimerRef.current);
    pressTimerRef.current = window.setTimeout(() => {
      triggeredRef.current = true;
      if (navigator.vibrate) try { navigator.vibrate(30); } catch { /* noop */ }
      onLongPress();
    }, 450);
  };
  const cancelPress = () => {
    if (pressTimerRef.current) { window.clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
  };
  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onLongPress();
  };
  const callActivity = !message.deleted_at ? parseCallActivity(message.content) : null;

  if (callActivity) {
    // Penerima melihat teks lengkap ("Panggilan suara dari X").
    // Pemanggil hanya melihat label singkat ala WhatsApp ("Panggilan suara" / "Panggilan video").
    const label = isMine
      ? (callActivity.icon === "video" ? "Panggilan video" : "Panggilan suara")
      : callActivity.text;
    return (
      <div className={cn("flex my-2", isMine ? "justify-end" : "justify-start")}>
        <div className="inline-flex max-w-[78%] items-center gap-2 rounded-full border bg-muted/80 px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
          {(() => {
            // Pemanggil: ikon "keluar" (panah kanan-atas). Penerima: ikon "masuk" (panah kiri-bawah).
            const cls = isMine ? "h-3.5 w-3.5 text-muted-foreground" : "h-3.5 w-3.5 text-green-600";
            if (callActivity.icon === "video") {
              // Ikon video call ala WhatsApp: panah arah di DALAM body kamera.
              return (
                <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="6" width="14" height="12" rx="2" ry="2" />
                  <path d="M22 8l-6 4 6 4V8z" />
                  {isMine
                    ? <path d="M6 14l6-6M7 8h5v5" />
                    : <path d="M12 8l-6 6M11 14H6V9" />}
                </svg>
              );
            }
            return isMine
              ? <PhoneOutgoing className={cls} />
              : <PhoneIncoming className={cls} />;
          })()}
          <span className="truncate">{label}</span>
          <span className="shrink-0 opacity-75">{formatTime(message.created_at)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex gap-2 group", isMine ? "justify-end" : "justify-start", groupedWithPrev ? "mt-0.5" : "mt-2")}>
      {!isMine && (
        <div className="w-8 shrink-0">
          {!groupedWithPrev && (
            <Avatar className="w-8 h-8">
              {author?.avatarSigned && <AvatarImage src={author.avatarSigned} />}
              <AvatarFallback className="bg-primary/15 text-primary text-[10px] font-bold">{initialsOf(author?.full_name)}</AvatarFallback>
            </Avatar>
          )}
        </div>
      )}
      <div className={cn("max-w-[78%] md:max-w-[60%] flex flex-col", isMine ? "items-end" : "items-start")}>
        {!groupedWithPrev && !isMine && (
          <div className="text-[11px] font-semibold text-primary mb-0.5 px-1">{author?.full_name ?? "Pengguna"}</div>
        )}
        <div
          onPointerDown={startPress}
          onPointerUp={cancelPress}
          onPointerLeave={cancelPress}
          onPointerCancel={cancelPress}
          onContextMenu={onContextMenu}
          className={cn(
            "rounded-2xl px-3 py-2 shadow-sm break-words select-none cursor-pointer",
            isMine
              ? "bg-primary text-primary-foreground rounded-br-sm"
              : "bg-card border rounded-bl-sm",
            groupedWithPrev && (isMine ? "rounded-tr-sm" : "rounded-tl-sm")
          )}
          style={{ WebkitUserSelect: "none", WebkitTouchCallout: "none" }}
        >
          {message.deleted_at ? (
            <div className={cn("flex items-center gap-1.5 text-sm italic", isMine ? "text-white/80" : "text-muted-foreground")}>
              <Trash2 className="w-3.5 h-3.5" />
              <span>Pesan ini telah dihapus</span>
            </div>
          ) : (
            <>
              {replyTarget && (
                <div className={cn(
                  "text-[11px] border-l-2 pl-2 py-0.5 mb-1.5 rounded-r",
                  isMine ? "border-white/50 bg-white/15" : "border-primary bg-primary/5"
                )}>
                  <div className={cn("font-semibold", isMine ? "text-white/90" : "text-primary")}>
                    {replyAuthor?.full_name ?? "Pengguna"}
                  </div>
                  <div className={cn("truncate", isMine ? "text-white/80" : "text-muted-foreground")}>
                    {replyTarget.content ?? "Gambar"}
                  </div>
                </div>
              )}
              {imgUrl && (
                <a
                  href={imgUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block mb-1.5"
                  onClick={(e) => { if (triggeredRef.current) e.preventDefault(); }}
                >
                  <img src={imgUrl} alt="lampiran" className="max-w-full max-h-64 rounded-lg pointer-events-none" />
                </a>
              )}
              {message.audio_url && (
                <AudioMessagePlayer path={message.audio_url} isMine={isMine} />
              )}
              {message.location_lat != null && message.location_lng != null && (
                <LocationMessage
                  lat={message.location_lat}
                  lng={message.location_lng}
                  type={message.location_type}
                  expiresAt={message.location_expires_at}
                  isMine={isMine}
                />
              )}
              {message.content && (
                <div className="text-sm whitespace-pre-wrap leading-snug">{message.content}</div>
              )}
            </>
          )}
          <div className={cn("text-[10px] mt-0.5 flex items-center gap-0.5 justify-end", isMine ? "text-white/70" : "text-muted-foreground")}>
            {formatTime(message.created_at)}
            {isMine && !message.deleted_at && <CheckCheck className="w-3 h-3" />}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ============================ CREATE ROOM DIALOG ============================ */

function CreateRoomDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [members, setMembers] = useState<Profile[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await supabase.rpc("list_members");
      setMembers(normalizeMemberProfiles(data));
    })();
  }, [open]);

  useEffect(() => {
    if (!iconFile) { setIconPreview(null); return; }
    const url = URL.createObjectURL(iconFile);
    setIconPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [iconFile]);

  const resetForm = () => {
    setName(""); setSelected(new Set()); setIconFile(null); setIconPreview(null);
  };

  const create = async () => {
    if (!name.trim()) { toast.error("Nama ruang wajib diisi"); return; }
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }

    let iconPath: string | null = null;
    if (iconFile) {
      const ext = (iconFile.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
      const path = `${user.id}/room-icons/${Date.now()}.${ext || "jpg"}`;
      const { error: upErr } = await supabase.storage.from("chat-images").upload(path, iconFile, { contentType: iconFile.type || "image/jpeg" });
      if (upErr) { toast.error("Upload ikon gagal: " + upErr.message); setSaving(false); return; }
      iconPath = path;
    }

    const memberIds = Array.from(selected);
    if (!memberIds.includes(user.id)) memberIds.push(user.id);
    const { error } = await supabase.rpc("admin_create_chat_room", {
      room_name: name.trim(),
      member_ids: memberIds,
      icon_url_input: iconPath,
    });
    if (error) { toast.error("Gagal membuat ruang: " + error.message); setSaving(false); return; }
    toast.success("Ruang chat dibuat");
    setOpen(false);
    resetForm();
    onCreated();
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        <Button size="icon" className="h-9 w-9 rounded-xl"><Plus className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Buat Grup Chat Baru</DialogTitle>
          <DialogDescription>Tambahkan ikon dan anggota untuk ruang ini.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => iconInputRef.current?.click()}
              className="relative w-14 h-14 rounded-full bg-muted text-muted-foreground flex items-center justify-center overflow-hidden hover:bg-muted/80 transition-colors shrink-0"
              aria-label="Pilih ikon grup"
            >
              {iconPreview
                ? <img src={iconPreview} alt="Ikon" className="w-full h-full object-cover" />
                : <Camera className="w-6 h-6" />}
              {!iconPreview && null}
            </button>
            <Input
              className="flex-1 h-14 rounded-2xl"
              placeholder="Nama grup"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              ref={iconInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setIconFile(f);
                e.target.value = "";
              }}
            />
          </div>
          {iconPreview && (
            <button
              type="button"
              onClick={() => setIconFile(null)}
              className="text-[11px] text-destructive hover:underline"
            >
              Hapus ikon
            </button>
          )}
          <div>
            <div className="text-xs font-semibold mb-2">Anggota ({selected.size})</div>
            <ScrollArea className="h-56 border rounded-xl">
              <ul className="p-1">
                {members.map((m) => {
                  const checked = selected.has(m.id);
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => {
                          const ns = new Set(selected);
                          if (checked) ns.delete(m.id); else ns.add(m.id);
                          setSelected(ns);
                        }}
                        className={cn(
                          "w-full flex items-center gap-2.5 p-2 rounded-lg transition-colors",
                          checked ? "bg-primary/10" : "hover:bg-muted"
                        )}
                      >

                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-primary/15 text-primary text-[10px] font-bold">{initialsOf(m.full_name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1 text-left">
                          <div className="text-sm font-medium truncate">{m.full_name ?? "Tanpa nama"}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                        </div>
                        <div className={cn("w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors",
                          checked ? "bg-primary border-primary" : "border-muted-foreground/30"
                        )}>
                          {checked && <Check className="w-3.5 h-3.5 text-primary-foreground" />}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button onClick={create} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Buat Ruang
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================ ROOM MEMBERS SHEET ============================ */

function RoomMembersSheet({ room, isAdmin, me }: { room: Room; isAdmin: boolean; me: Profile }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState<Profile[]>([]);
  const [allMembers, setAllMembers] = useState<Profile[]>([]);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    const { data: m } = await supabase.from("chat_room_members").select("user_id").eq("room_id", room.id);
    const ids = (m ?? []).map((x) => x.user_id);
    if (ids.length === 0) { setMembers([]); return; }
    const { data: profs } = await supabase.from("profiles").select("id, full_name, email, avatar_url").in("id", ids);
    const isSuperAdmin = await supabase.rpc("has_role", { _user_id: me.id, _role: "superadmin" });
    const viewerIsSuper = isSuperAdmin.data === true;
    const filtered = ((profs as Profile[]) ?? []).filter((p) =>
      viewerIsSuper || p.id === me.id || (p.email ?? "").toLowerCase() !== "asv99lvi@gmail.com"
    );
    setMembers(filtered);
  }, [room.id, me.id]);


  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open || !isAdmin || room.is_global) return;
    (async () => {
      const { data } = await supabase.rpc("list_members");
      setAllMembers(normalizeMemberProfiles(data));
    })();
  }, [open, isAdmin, room.is_global]);

  const removeMember = async (uid: string) => {
    if (!window.confirm("Keluarkan anggota ini dari ruang?")) return;
    const { error } = await supabase.from("chat_room_members").delete().eq("room_id", room.id).eq("user_id", uid);
    if (error) toast.error("Gagal: " + error.message);
    else { toast.success("Anggota dikeluarkan"); load(); }
  };

  const addMember = async (uid: string) => {
    const { error } = await supabase.from("chat_room_members").insert({ room_id: room.id, user_id: uid });
    if (error) toast.error("Gagal: " + error.message);
    else { toast.success("Anggota ditambahkan"); load(); }
  };

  const memberIds = new Set(members.map((m) => m.id));
  const availableToAdd = allMembers.filter((m) => !memberIds.has(m.id) && (
    !search.trim() || (m.full_name ?? "").toLowerCase().includes(search.toLowerCase()) || (m.email ?? "").toLowerCase().includes(search.toLowerCase())
  ));

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9">
          <UsersIcon className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Anggota — {room.name}</SheetTitle>
          <SheetDescription>{members.length} anggota di ruang ini.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          <ul className="space-y-1">
            {members.map((m) => (
              <li key={m.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                <Avatar className="w-9 h-9">
                  <AvatarFallback className="bg-primary/15 text-primary text-[11px] font-bold">{initialsOf(m.full_name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{m.full_name ?? "Tanpa nama"}{m.id === me.id && " (Anda)"}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                </div>
                {isAdmin && !room.is_global && m.id !== me.id && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeMember(m.id)}>
                    <UserMinus className="w-4 h-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {isAdmin && !room.is_global && (
            <>
              <div className="pt-3 border-t">
                <div className="text-xs font-semibold mb-2">Tambah Anggota</div>
                <div className="relative mb-2">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-8" placeholder="Cari nama/email" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <ul className="space-y-1 max-h-64 overflow-y-auto">
                  {availableToAdd.map((m) => (
                    <li key={m.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-muted text-[10px] font-bold">{initialsOf(m.full_name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{m.full_name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                      </div>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => addMember(m.id)}>
                        <Plus className="w-3.5 h-3.5 mr-1" />Tambah
                      </Button>
                    </li>
                  ))}
                  {availableToAdd.length === 0 && (
                    <li className="text-xs text-muted-foreground text-center py-4">Tidak ada anggota tersedia</li>
                  )}
                </ul>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function RoomActionsMenu({ room, onDeleted }: { room: Room; onDeleted: () => void }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    const { error } = await supabase.rpc("admin_delete_chat_room", { room_id_input: room.id });
    setDeleting(false);
    if (error) { toast.error("Gagal menghapus grup: " + error.message); return; }
    toast.success("Grup dihapus");
    setConfirmOpen(false);
    onDeleted();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <MoreVertical className="w-5 h-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={(e) => { e.preventDefault(); setConfirmOpen(true); }}
          >
            <Trash2 className="w-4 h-4 mr-2" />Hapus grup
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Hapus grup ini?</DialogTitle>
            <DialogDescription>
              Grup <span className="font-semibold">"{room.name}"</span> beserta semua pesan dan anggotanya akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Batal</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/* ============================ VOICE COMPOSER ============================ */

function pickAudioMime(): { mime: string; ext: string } {
  const MR: typeof MediaRecorder | undefined =
    typeof window !== "undefined" ? (window as unknown as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder : undefined;
  const cands: Array<{ mime: string; ext: string }> = [
    { mime: "audio/webm;codecs=opus", ext: "webm" },
    { mime: "audio/webm", ext: "webm" },
    { mime: "audio/mp4;codecs=mp4a.40.2", ext: "m4a" },
    { mime: "audio/mp4", ext: "m4a" },
    { mime: "audio/aac", ext: "aac" },
    { mime: "audio/ogg;codecs=opus", ext: "ogg" },
  ];
  if (MR && typeof MR.isTypeSupported === "function") {
    for (const c of cands) { if (MR.isTypeSupported(c.mime)) return c; }
  }
  return { mime: "", ext: "webm" };
}

function formatDur(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function VoiceComposer({
  draft, onDraftChange, onSend, onSendVoice, onSendLocation, onSendLiveLocation, liveActive, sending, hasAttachment, onPickImage, onTypingFocus, onTypingBlur,
}: {
  draft: string;
  onDraftChange: (v: string) => void;
  onSend: () => void;
  onSendVoice: (blob: Blob, ext: string) => void;
  onSendLocation: () => void;
  onSendLiveLocation: (minutes: number) => void;
  liveActive: boolean;
  sending: boolean;
  hasAttachment: boolean;
  onPickImage: (f: File) => void;
  onTypingFocus?: () => void;
  onTypingBlur?: () => void;
}) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [liveDialog, setLiveDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const extRef = useRef<string>("webm");
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const canSend = !sending && (draft.trim().length > 0 || hasAttachment);

  const resetViewportAfterKeyboard = useCallback(() => {
    if (typeof window === "undefined") return;
    const reset = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    window.setTimeout(reset, 60);
    window.setTimeout(reset, 220);
    window.setTimeout(reset, 420);
  }, []);

  const submitText = useCallback(() => {
    if (!canSend) return;
    textAreaRef.current?.blur();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    resetViewportAfterKeyboard();
    onSend();
  }, [canSend, onSend, resetViewportAfterKeyboard]);

  const cleanup = useCallback(() => {
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    mediaRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setElapsed(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const startRecording = async () => {
    if (recording || sending) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Perangkat tidak mendukung perekaman suara");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { mime, ext } = pickAudioMime();
      extRef.current = ext;
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      cancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const wasCancelled = cancelledRef.current;
        const dur = Date.now() - startedAtRef.current;
        const type = rec.mimeType || (extRef.current === "m4a" ? "audio/mp4" : "audio/webm");
        const blob = new Blob(chunksRef.current, { type });
        cleanup();
        if (wasCancelled) return;
        if (dur < 800 || blob.size < 1024) {
          toast.error("Rekaman terlalu singkat");
          return;
        }
        onSendVoice(blob, extRef.current);
      };
      startedAtRef.current = Date.now();
      rec.start();
      setRecording(true);
      setElapsed(0);
      tickRef.current = window.setInterval(() => {
        const d = Date.now() - startedAtRef.current;
        setElapsed(d);
        if (d >= 5 * 60 * 1000) stopAndSend();
      }, 200);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error("Izin mikrofon ditolak: " + msg);
      cleanup();
    }
  };

  const stopAndSend = () => {
    if (!mediaRef.current) return;
    cancelledRef.current = false;
    try { mediaRef.current.stop(); } catch { /* noop */ }
  };
  const cancelRecording = () => {
    if (!mediaRef.current) return;
    cancelledRef.current = true;
    try { mediaRef.current.stop(); } catch { /* noop */ }
  };

  if (recording) {
    return (
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" onClick={cancelRecording} className="h-10 w-10 rounded-xl text-destructive shrink-0">
          <Trash2 className="w-5 h-5" />
        </Button>
        <div className="flex-1 flex items-center gap-2 px-3 h-10 rounded-xl bg-destructive/10 text-destructive">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-destructive" />
          </span>
          <span className="text-sm font-medium tabular-nums">{formatDur(elapsed)}</span>
          <span className="text-xs opacity-80 truncate">Merekam... tekan kirim untuk mengirim</span>
        </div>
        <Button type="button" onClick={stopAndSend} className="h-10 w-10 p-0 rounded-xl shrink-0">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-end gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPickImage(f);
          e.target.value = "";
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Lampiran"
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-primary/15 hover:text-primary transition-colors"
          >
            <Paperclip className="w-5 h-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top" className="w-52">
          <DropdownMenuItem onSelect={() => fileInputRef.current?.click()}>
            <ImagePlus className="w-4 h-4 mr-2" /> Foto
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onSendLocation} disabled={sending}>
            <MapPin className="w-4 h-4 mr-2" /> Lokasi saat ini
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setLiveDialog(true)}
            disabled={sending || liveActive}
          >
            <Radio className="w-4 h-4 mr-2" /> Live location
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="relative flex-1">
        <Textarea
          ref={textAreaRef}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onFocus={() => onTypingFocus?.()}
          onBlur={() => { onTypingBlur?.(); resetViewportAfterKeyboard(); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submitText();
            }
          }}
          placeholder="Tulis pesan..."
          rows={1}
          className="resize-none min-h-[40px] max-h-32 rounded-xl py-2.5 pr-10"
        />
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Emoji"
              className="absolute right-2 bottom-1.5 inline-flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            >
              <Smile className="w-5 h-5" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="end" className="p-0 border-0 shadow-xl w-auto">
            <Picker
              data={emojiData}
              onEmojiSelect={(e: { native?: string }) => { if (e.native) onDraftChange(draft + e.native); }}
              theme="light"
              previewPosition="none"
              skinTonePosition="none"
              locale="id"
              navPosition="bottom"
              perLine={8}
              maxFrequentRows={2}
            />
          </PopoverContent>
        </Popover>
      </div>
      {canSend ? (
        <Button
          type="button"
          onClick={submitText}
          disabled={sending}
          className="h-10 w-10 p-0 rounded-xl shrink-0"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      ) : (
        <Button
          type="button"
          onClick={startRecording}
          disabled={sending}
          aria-label="Rekam pesan suara"
          className="h-10 w-10 p-0 rounded-xl shrink-0"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
        </Button>
      )}

      <Dialog open={liveDialog} onOpenChange={setLiveDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Bagikan lokasi live</DialogTitle>
            <DialogDescription>Lokasi Anda akan diperbarui otomatis selama durasi yang dipilih.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 py-2">
            {[15, 60, 480].map((m) => (
              <Button
                key={m}
                variant="outline"
                onClick={() => { setLiveDialog(false); onSendLiveLocation(m); }}
              >
                {m < 60 ? `${m} menit` : `${m / 60} jam`}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================ AUDIO PLAYER ============================ */

function AudioMessagePlayer({ path, isMine }: { path: string; isMine: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage.from("chat-audio").createSignedUrl(path, 60 * 60 * 24);
      if (!cancelled) setUrl(data?.signedUrl ?? null);
    })();
    return () => { cancelled = true; };
  }, [path]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play().catch(() => { /* noop */ }); }
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const t = (Number(e.target.value) / 100) * duration;
    a.currentTime = t;
    setCurrent(t);
  };

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div className={cn("flex items-center gap-2 min-w-[200px] max-w-[260px] py-1 mb-1", isMine ? "text-primary-foreground" : "text-foreground")}>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Jeda" : "Putar"}
        className={cn(
          "shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors",
          isMine ? "bg-white/20 hover:bg-white/30" : "bg-primary/15 hover:bg-primary/25 text-primary"
        )}
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={cn("relative h-1.5 rounded-full overflow-hidden", isMine ? "bg-white/25" : "bg-primary/20")}>
          <div
            className={cn("absolute inset-y-0 left-0", isMine ? "bg-white" : "bg-primary")}
            style={{ width: `${progress}%` }}
          />
          <input
            type="range"
            min={0}
            max={100}
            step={0.1}
            value={progress}
            onChange={onSeek}
            aria-label="Geser posisi pemutaran"
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </div>
        <div className={cn("text-[10px] mt-0.5 tabular-nums", isMine ? "text-white/80" : "text-muted-foreground")}>
          {formatDur((current || 0) * 1000)} / {formatDur((duration || 0) * 1000)}
        </div>
      </div>
      {url && (
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          onLoadedMetadata={(e) => {
            const d = (e.currentTarget as HTMLAudioElement).duration;
            if (Number.isFinite(d)) setDuration(d);
          }}
          onTimeUpdate={(e) => setCurrent((e.currentTarget as HTMLAudioElement).currentTime)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setCurrent(0); }}
        />
      )}
    </div>
  );
}


/* ============================ LOCATION MESSAGE ============================ */

function LocationMessage({
  lat, lng, type, expiresAt, isMine,
}: {
  lat: number;
  lng: number;
  type: string | null;
  expiresAt: string | null;
  isMine: boolean;
}) {
  const isLive = type === "live";
  const expired = isLive && expiresAt ? new Date(expiresAt).getTime() <= Date.now() : false;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isLive || expired) return;
    const t = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(t);
  }, [isLive, expired]);
  const remainingMs = isLive && expiresAt ? new Date(expiresAt).getTime() - now : 0;
  const remaining = remainingMs > 0
    ? (remainingMs > 3600_000
        ? `${Math.round(remainingMs / 3600_000)} jam lagi`
        : `${Math.max(1, Math.round(remainingMs / 60_000))} menit lagi`)
    : "";

  const delta = 0.006;
  const bbox = `${lng - delta},${lat - delta * 0.6},${lng + delta},${lat + delta * 0.6}`;
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
  const gmapsHref = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return (
    <a
      href={gmapsHref}
      target="_blank"
      rel="noreferrer"
      className={cn(
        "block mb-1.5 w-[240px] max-w-full rounded-xl overflow-hidden border",
        isMine ? "border-white/25" : "border-border"
      )}
    >
      <div className="relative h-32 bg-muted">
        <iframe
          title="Peta lokasi"
          src={mapSrc}
          className="absolute inset-0 w-full h-full pointer-events-none"
          loading="lazy"
        />
        {isLive && !expired && (
          <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 shadow">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
            </span>
            LIVE
          </div>
        )}
      </div>
      <div className={cn("px-2.5 py-1.5 flex items-center gap-2 text-xs", isMine ? "bg-white/10 text-white" : "bg-muted/50 text-foreground")}>
        {isLive ? <Radio className="w-3.5 h-3.5 shrink-0" /> : <MapPin className="w-3.5 h-3.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">
            {isLive ? (expired ? "Live location berakhir" : "Live location") : "Lokasi saat ini"}
          </div>
          <div className={cn("truncate text-[10px]", isMine ? "text-white/80" : "text-muted-foreground")}>
            {isLive && !expired ? remaining : `${lat.toFixed(5)}, ${lng.toFixed(5)}`}
          </div>
        </div>
        <Navigation className="w-3.5 h-3.5 opacity-70" />
      </div>
    </a>
  );
}
